import { CheckIcon } from '@radix-ui/react-icons';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Radio,
  Separator,
  Switch,
  Text,
} from '@radix-ui/themes';
import { CommonInputHint } from 'components/common/form/hint';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonInputWrapper } from 'components/common/form/wrapper';
import { t } from 'i18next';
import { IChecklistInput } from 'interfaces/forms/checklist';
import { IOptionExt } from 'interfaces/forms/option-ext';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { v4 } from 'uuid';

const Wrapper = (props: {
  label: ReactNode;
  suffix?: ReactNode;
  onClickLabel: () => void;
  control: ReactNode;
}) => (
  <Flex gap={RADIX.FLEX.GAP.SM} align="center" pl="1" pr="1">
    {props.control}
    <Text className="cursor-pointer" onClick={props.onClickLabel} truncate>
      {props.label}
      {props.suffix}
    </Text>
  </Flex>
);

export const CommonChecklist = (props: IChecklistInput) => {
  const [id] = useState(v4());

  const safeOptions = useMemo(() => {
    const { truncateTo = 0, options, values } = props;

    const visible = options.filter((o) => !o.invisible);

    if (truncateTo <= 0) {
      // don't truncate
      return visible;
    }

    if (visible.length <= truncateTo) {
      // no need to truncate
      return visible;
    }

    const selectedDict: { [key: string]: boolean | undefined } = {};

    // faster than iterating over all the options, most of which will be deselected
    values.forEach((v) => {
      selectedDict[v] = true;
    });

    // only keep up to the first X options that are not group entries and are not selected
    const deselected = visible
      .filter((o) => !o.isGroup && !selectedDict[o.value])
      .filter((_, i) => i < truncateTo);

    const deselectedDict: { [key: string]: boolean | undefined } = {};

    deselected.forEach((o) => {
      deselectedDict[o.value] = true;
    });

    // keep all selected options
    const selected = visible.filter((o) => selectedDict[o.value]);

    // keep all groups for any options from above
    const visibleGroups = ArrayHelper.unique(
      [...deselected, ...selected].map((o) => o.group)
    );

    const output: IOptionExt[] = visible.filter((o) => {
      if (o.isGroup && visibleGroups.includes(o.group)) {
        return true;
      }

      if (selectedDict[o.value]) {
        return true;
      }

      if (deselectedDict[o.value]) {
        return true;
      }

      return false;
    });

    return output;
  }, [props.truncateTo, props.options, props.values]);

  const handleChecked = useCallback(
    (config: { value: string; index: number; nextChecked: boolean }) => {
      // uncheck behaviour is the same regardless of hierarchy mode
      if (!config.nextChecked) {
        // remove the unchecked value from the list
        const nextValues = props.values.filter((v) => v !== config.value);
        props.onChange(nextValues);
        return;
      }

      if (props.singleSearch) {
        // if operating w/n single mode (e.g. for a non-multiple search component) then only the latest value matters
        props.onChange([config.value]);
        return;
      }

      // check behaviour depends on hierarchy mode
      switch (props.hierarchy) {
        case 'asc': {
          // add the checked value and everything after it to the list
          const nextValues = ArrayHelper.unique([
            ...props.values,
            ...safeOptions
              .filter(
                (o, i) =>
                  i >= config.index &&
                  // ignore disabled options
                  !o.disabled &&
                  // ignore separators
                  !o.label.startsWith('_')
              )
              .map((o) => o.value),
          ]);

          props.onChange(nextValues);
          return;
        }

        case 'desc': {
          // add the checked value and everything before it to the list
          const nextValues = ArrayHelper.unique([
            ...props.values,
            ...safeOptions
              .filter(
                (o, i) =>
                  i <= config.index &&
                  // ignore disabled options
                  !o.disabled &&
                  // ignore separators
                  !o.label.startsWith('_')
              )
              .map((o) => o.value),
          ]);

          props.onChange(nextValues);
          return;
        }

        default: {
          // add only the checked value to the list
          const nextValues = ArrayHelper.unique([
            ...props.values,
            config.value,
          ]);

          props.onChange(nextValues);
          return;
        }
      }
    },
    [props.hierarchy, props.values, safeOptions]
  );

  // radios do not support multiple selections or unchecking
  const onChangeRadio = useCallback(
    (value: string) => props.onChange([value]),
    [props.onChange]
  );

  // only indent if we're displaying options under a group heading
  const shouldIndent = safeOptions.some((o) => o.isGroup);

  return (
    <CommonInputWrapper {...props}>
      <CommonInputLabel {...props} />

      <Flex direction="column" gap={RADIX.FLEX.GAP.INPUT} maxWidth="inherit">
        {safeOptions.map((option, i) => {
          if (option.isGroup && option.label.startsWith('_')) {
            return <Separator key={`${id}-${i}`} size="4" />;
          }

          const isChecked = props.values.includes(option.value);

          if (props.as === 'button') {
            const isGroupOption = option.group && !option.isGroup;

            return (
              <Button
                id={`${id}-${i}`}
                key={`${id}-${i}`}
                className={StringHelper.classNames([
                  'width-100p',
                  'btn-checklist',
                  isChecked ? 'btn-checklist-checked' : '',
                ])}
                size={props.btnSize}
                style={{
                  justifyContent: 'start',
                }}
                disabled={props.disabled || option.disabled}
                onClick={() =>
                  handleChecked({
                    value: option.value,
                    index: i,
                    nextChecked: true,
                  })
                }
                variant="soft"
                color={option.color}
              >
                <Flex
                  align="center"
                  style={{ width: '100%' }}
                  // indent group options for hierarchy
                  pl={
                    isGroupOption && shouldIndent
                      ? RADIX.FLEX.PAD.SM
                      : undefined
                  }
                  gap={RADIX.FLEX.GAP.XS}
                >
                  {isChecked && <CheckIcon height="20" width="20" />}
                  {option.prefix && (
                    <Box mr={RADIX.FLEX.PAD.XS}>{option.prefix}</Box>
                  )}
                  <Text className={option.className} truncate>
                    {t(option.label)}
                  </Text>
                  {option.suffix && (
                    <Box ml="auto" pl={RADIX.FLEX.PAD.XS}>
                      {option.suffix}
                    </Box>
                  )}
                </Flex>
              </Button>
            );
          }

          const onClickLabel = () => {
            if (props.disabled) {
              return;
            }

            if (option.disabled) {
              return;
            }

            if (props.as === 'radio') {
              onChangeRadio(option.value);
              return;
            }

            // everything else toggles on/off as usual
            handleChecked({
              value: option.value,
              index: i,
              nextChecked: !isChecked,
            });
          };

          const labelNode = (
            <Text
              className={option.className}
              color={
                option.disabled || option.hideControl
                  ? RADIX.COLOR.NEUTRAL
                  : option.color
              }
              truncate
            >
              {t(option.label)}
            </Text>
          );

          switch (props.as) {
            case 'radio': {
              return (
                <Wrapper
                  key={`${id}-${i}`}
                  label={labelNode}
                  suffix={option.suffix}
                  onClickLabel={onClickLabel}
                  control={
                    <Radio
                      name={id}
                      value={option.value}
                      disabled={option.disabled}
                      onChange={() => onChangeRadio(option.value)}
                      checked={props.values.includes(option.value)}
                    />
                  }
                />
              );
            }

            case 'switch': {
              return (
                <Wrapper
                  key={`${id}-${i}`}
                  label={labelNode}
                  suffix={option.suffix}
                  onClickLabel={onClickLabel}
                  control={
                    option.hideControl ? (
                      <></>
                    ) : (
                      <Switch
                        name={props.name}
                        disabled={props.disabled}
                        checked={props.values.includes(option.value)}
                        onCheckedChange={(checked) =>
                          handleChecked({
                            value: option.value,
                            index: i,
                            nextChecked: checked,
                          })
                        }
                      />
                    )
                  }
                />
              );
            }

            case 'checkbox':
            default: {
              return (
                <Wrapper
                  key={`${id}-${i}`}
                  label={labelNode}
                  suffix={option.suffix}
                  onClickLabel={onClickLabel}
                  control={
                    <Checkbox
                      id={`${id}-${i}`}
                      title={t(option.label).toString()}
                      disabled={props.disabled || option.disabled}
                      checked={props.values.includes(option.value)}
                      style={{
                        // hides the control while preserving the spacing
                        visibility: option.hideControl ? 'hidden' : 'visible',
                        // left-aligns the group label to maintain hierarchy
                        display: option.isGroup ? 'none' : 'initial',
                      }}
                      onCheckedChange={(checked) =>
                        handleChecked({
                          value: option.value,
                          index: i,
                          nextChecked: ['indeterminate', true].includes(
                            checked
                          ),
                        })
                      }
                    />
                  }
                />
              );
            }
          }
        })}
      </Flex>

      <CommonInputHint {...props} />
    </CommonInputWrapper>
  );
};
